import { MEASUREMENT_TYPES } from "../config/enums/MeasurementTypes";
import { IWasteForm } from "../components/wasteManagment/wasteManagement.types";
import {
  createEmptyWasteRecord,
  mapWasteRecordFromBackend,
  normalizeWasteUnit,
  sumWasteDistributions
} from "../components/wasteManagment/wasteManagement.utils";

export const WASTE_FORM_SYNC_INIT_SIGNATURE = "INIT";

type SideStreamUnitValue = IWasteForm["sideStreamUnit"];

export function buildWasteRecordsSignature(
  records: Array<{ _id?: string; sideStreamsDistribution?: unknown; unit?: unknown }>
): string {
  return records
    .map(r => `${r._id}:${r.sideStreamsDistribution ?? ""}:${r.unit ?? ""}`)
    .join(",");
}

export function resolveWasteFormGlobalUnit(
  existingRecords: any[],
  parentSideStreamUnit: SideStreamUnitValue | undefined,
  currentSideStreamUnit: SideStreamUnitValue | undefined
): MEASUREMENT_TYPES {
  if (parentSideStreamUnit !== undefined) {
    return normalizeWasteUnit(parentSideStreamUnit, MEASUREMENT_TYPES.WEIGHT_KG);
  }

  if (currentSideStreamUnit !== undefined && currentSideStreamUnit !== null && currentSideStreamUnit !== "") {
    return normalizeWasteUnit(currentSideStreamUnit, MEASUREMENT_TYPES.WEIGHT_KG);
  }

  if (existingRecords.length > 0 && existingRecords[0].unit) {
    return normalizeWasteUnit(existingRecords[0].unit, MEASUREMENT_TYPES.WEIGHT_KG);
  }

  return MEASUREMENT_TYPES.WEIGHT_KG;
}

export function getParentSideStreamUnitToSync(
  parentSideStreamUnit: SideStreamUnitValue | undefined,
  currentSideStreamUnit: SideStreamUnitValue | undefined
): MEASUREMENT_TYPES | null {
  if (parentSideStreamUnit === undefined) return null;

  const parentUnit = normalizeWasteUnit(parentSideStreamUnit, MEASUREMENT_TYPES.WEIGHT_KG);
  const internalUnit = normalizeWasteUnit(currentSideStreamUnit, MEASUREMENT_TYPES.WEIGHT_KG);

  return parentUnit !== internalUnit ? parentUnit : null;
}

export function buildWasteFormResetState(
  existingRecords: any[],
  sideStreamValue: unknown,
  defaultTypeId: string,
  parentSideStreamUnit: SideStreamUnitValue | undefined,
  currentSideStreamUnit: SideStreamUnitValue | undefined
): IWasteForm {
  const mappedRecords = existingRecords.map(mapWasteRecordFromBackend);
  const resolvedUnit = resolveWasteFormGlobalUnit(
    existingRecords,
    parentSideStreamUnit,
    currentSideStreamUnit
  );
  const total = sumWasteDistributions(mappedRecords, resolvedUnit, resolvedUnit);

  return {
    wastes: [...mappedRecords, createEmptyWasteRecord(defaultTypeId, resolvedUnit)],
    totalSide: total || null,
    sideStream: sideStreamValue,
    sideStreamUnit: resolvedUnit
  };
}

/** Input context for planning waste form sync actions (pure data, no side effects). */
export type WasteFormSyncInput = {
  accordionEnabled: boolean;
  existingRecords: any[];
  sideStreamValue: unknown;
  defaultTypeId: string;
  parentSideStreamUnit?: number | string;
  lastRecordSignature: string;
  currentSideStreamUnit: SideStreamUnitValue;
};

/** Discriminated union describing which sync action the hook should apply. */
export type WasteFormSyncPlan =
  | { action: "noop"; nextSignature: string }
  | {
      action: "patch";
      sideStream: unknown;
      sideStreamUnit: MEASUREMENT_TYPES | null;
      nextSignature: string;
    }
  | {
      action: "reset";
      formState: IWasteForm;
      nextSignature: string;
    };

/**
 * Plans a noop sync when the waste accordion is closed.
 *
 * Resets the record signature so the next accordion open triggers a full form reset.
 *
 * @returns A noop plan with signature set to WASTE_FORM_SYNC_INIT_SIGNATURE
 */
export function planWasteFormSyncWhenDisabled(): WasteFormSyncPlan {
  return {
    action: "noop",
    nextSignature: WASTE_FORM_SYNC_INIT_SIGNATURE
  };
}

/**
 * Plans a patch-only sync when backend records have not changed.
 *
 * Updates sideStream and optionally sideStreamUnit without a full form reset.
 *
 * @param input - Current sync context
 * @param signature - Current records signature (unchanged from last sync)
 * @returns A patch plan with sideStream and optional sideStreamUnit updates
 */
export function planWasteFormSyncPatch(
  input: WasteFormSyncInput,
  signature: string
): WasteFormSyncPlan {
  return {
    action: "patch",
    sideStream: input.sideStreamValue,
    sideStreamUnit: getParentSideStreamUnitToSync(
      input.parentSideStreamUnit,
      input.currentSideStreamUnit
    ),
    nextSignature: signature
  };
}

/**
 * Plans a full form reset when backend records have changed.
 *
 * Rebuilds the entire form state from mapped records plus a blank draft row.
 *
 * @param input - Current sync context
 * @param signature - New records signature after the change
 * @returns A reset plan with the rebuilt form state
 */
export function planWasteFormSyncReset(
  input: WasteFormSyncInput,
  signature: string
): WasteFormSyncPlan {
  return {
    action: "reset",
    formState: buildWasteFormResetState(
      input.existingRecords,
      input.sideStreamValue,
      input.defaultTypeId,
      input.parentSideStreamUnit,
      input.currentSideStreamUnit
    ),
    nextSignature: signature
  };
}

/**
 * Builds a sync plan for the waste form based on accordion state and record changes.
 *
 * Pure decision logic — no form side effects. The hook applies the returned plan.
 *
 * @param input - Current sync context (records, signature, parent unit, etc.)
 * @returns A discriminated plan: noop (accordion closed), patch (records unchanged), or reset (records changed)
 */
export function planWasteFormSync(input: WasteFormSyncInput): WasteFormSyncPlan {
  if (!input.accordionEnabled) {
    return planWasteFormSyncWhenDisabled();
  }

  const signature = buildWasteRecordsSignature(input.existingRecords);

  if (signature === input.lastRecordSignature) {
    return planWasteFormSyncPatch(input, signature);
  }

  return planWasteFormSyncReset(input, signature);
}
